// ══════════════════════════════════════════════════════
// R&D PROJECTS API — matched to RdProjectsController.cs
// ══════════════════════════════════════════════════════
// VERIFIED ENDPOINTS from controller:
//   GET    /api/rd-projects
//   GET    /api/rd-projects/{id}            ← includes sections, milestones, budgetLines, etc.
//   POST   /api/rd-projects
//   PUT    /api/rd-projects/{id}
//   DELETE /api/rd-projects/{id}
//   PUT    /api/rd-projects/sections/{sectionId}   ← update ONE section by ID
//   POST   /api/rd-projects/{id}/submit
//   POST   /api/rd-projects/{id}/decide            ← approve/decline/return (RdApprovalDecisionDto)
//   POST   /api/rd-projects/{pid}/milestones
//   PUT    /api/rd-projects/milestones/{id}
//   DELETE /api/rd-projects/milestones/{id}
//   POST   /api/rd-projects/{pid}/budget-lines     ← NOT /budget
//   PUT    /api/rd-projects/budget-lines/{id}
//   DELETE /api/rd-projects/budget-lines/{id}
//   POST   /api/rd-projects/{pid}/outputs
//   PUT    /api/rd-projects/{pid}/strategic-objectives
//   PUT    /api/rd-projects/{pid}/programmes
//   PUT    /api/rd-projects/{pid}/strategy-tags
//   GET    /api/rd-projects/{pid}/comments
//   POST   /api/rd-projects/{pid}/comments
//   POST   /api/rd-projects/{pid}/risks
//   POST   /api/rd-projects/{pid}/documents
//   POST   /api/rd-projects/{pid}/team
// ══════════════════════════════════════════════════════

import api from './api';

const BASE = '/rd-projects';

const safe = async (promise, defaultVal = null) => {
  try { return await promise; } catch { return { data: defaultVal }; }
};

// ══════════════════════════════════════════════════════
// ENUM MAPPERS
// ══════════════════════════════════════════════════════

const FUNDING_CATEGORY_MAP = {
  'New Research Initiative': 'NewResearch',
  'Continuation of Existing Project': 'ContinuationProject',
  'Pilot / Feasibility Study': 'PilotFeasibility',
  'Collaborative Research': 'CollaborativeResearch',
  'Technology Demonstration': 'TechnologyDemonstration',
};
const FUNDING_CATEGORY_REVERSE = Object.fromEntries(Object.entries(FUNDING_CATEGORY_MAP).map(([k, v]) => [v, k]));

const CONFIDENTIALITY_MAP = { 'Public': 'Public', 'Internal': 'Internal', 'Confidential': 'Confidential', 'Strictly Confidential': 'StrictlyConfidential' };
const CONFIDENTIALITY_REVERSE = Object.fromEntries(Object.entries(CONFIDENTIALITY_MAP).map(([k, v]) => [v, k]));

// ══════════════════════════════════════════════════════
// OUTGOING — form → RdProject model
// ══════════════════════════════════════════════════════

function mapProjectToApi(form) {
  // ── Phase C+: Year-by-year planning (create only — ignored on update) ──
  // When the form has yearPlans with at least one outcome OR budget, we forward
  // them. If any plannedBudget is set and the user didn't enter TotalBudget
  // directly, auto-sum. Backend also does this guard, but computing here keeps
  // the response payload consistent.
  const yearPlansRaw = Array.isArray(form.yearPlans) ? form.yearPlans : [];
  const yearPlans = yearPlansRaw
    .filter(p => p && p.yearNumber >= 1 && ((p.expectedOutcome && p.expectedOutcome.trim()) || p.plannedBudget !== '' && p.plannedBudget != null))
    .map(p => ({
      yearNumber: parseInt(p.yearNumber),
      plannedBudget: (p.plannedBudget === '' || p.plannedBudget == null) ? null : parseFloat(p.plannedBudget),
      expectedOutcome: p.expectedOutcome ? p.expectedOutcome.trim() : null,
    }));

  const perYearBudgetSum = yearPlans
    .filter(p => p.plannedBudget != null)
    .reduce((s, p) => s + p.plannedBudget, 0);
  const explicitTotalBudget = form.totalBudget ? parseFloat(form.totalBudget) : null;
  const totalBudget = (perYearBudgetSum > 0 && (!explicitTotalBudget || explicitTotalBudget === 0))
    ? perYearBudgetSum
    : explicitTotalBudget;

  return {
    title: form.title || '',
    projectType: form.projectType || 'Research',
    status: form.status || 'Draft',
    piName: form.principalInvestigator || '',
    piEmail: form.piEmail || '',
    piPhone: form.piPhone || '',
    piEmployeeNumber: form.piEmployeeNumber || '',
    researchGroup: form.researchTheme || '',          // legacy free text (kept for backward compat)
    researchThemeId: form.researchThemeId || null,    // V3.1 — admin-managed FK
    clusterId: form.clusterId || null,                // V3.1 — Cluster FK
    priorGrantReference: form.priorGrant || null,     // V3.1 — Yes/No marker for prior Mintek grant
    coInvestigators: form.coInvestigators || '',
    divisionId: form.divisionId || null,
    financialYear: parseInt(form.financialYear) || 2026,
    totalYears: parseInt(form.totalYears) || 1,
    startDate: form.startDate || null,
    endDate: form.endDate || null,
    trlCurrent: form.trlStage ? parseInt(form.trlStage) : null,
    trlTarget: form.trlTarget ? parseInt(form.trlTarget) : null,
    totalBudget: totalBudget,
    fundingSource: form.fundingSource || '',
    fundingCategory: FUNDING_CATEGORY_MAP[form.fundingCategory] || form.fundingCategory || null,
    clientName: form.clientName || null,
    clientContactName: form.clientContactName || null,
    clientContactEmail: form.clientContactEmail || null,
    clientContactPhone: form.clientContactPhone || null,
    totalProjectValue: totalBudget,
    costCentreCode: form.costCentreCode || null,
    confidentialityLevel: CONFIDENTIALITY_MAP[form.confidentialityLevel] || form.confidentialityLevel || 'Internal',
    // Phase C+: forwarded only on Create; Update controller ignores YearPlans.
    yearPlans: yearPlans.length > 0 ? yearPlans : null,
  };
}

// ══════════════════════════════════════════════════════
// INCOMING — RdProject response → frontend aliases
// ══════════════════════════════════════════════════════

function mapProjectFromApi(p) {
  if (!p) return p;
  // Extract form data from sections (already included in GET {id} response)
  const sectionData = extractFormFromSections(p.sections);
  return {
    ...p,
    reference: p.referenceNo || p.reference || '',
    principalInvestigator: p.piName || '',
    projectManager: p.piName || '',
    trlStage: p.trlCurrent ?? null,
    trlTarget: p.trlTarget ?? null,
    researchTheme: p.researchTheme || p.researchGroup || '',  // FK name first, free-text fallback
    researchThemeId: p.researchThemeId || null,                // V3.1
    clusterId: p.clusterId || null,                            // V3.1
    clusterName: p.clusterName || null,                        // V3.1
    actualSpend: p.totalActualSpend ?? 0,
    fundingCategory: FUNDING_CATEGORY_REVERSE[p.fundingCategory] || p.fundingCategory || '',
    confidentialityLevel: CONFIDENTIALITY_REVERSE[p.confidentialityLevel] || p.confidentialityLevel || 'Internal',
    budgetLines: (p.budgetLines || []).map(b => ({ ...b, budgetAmount: b.plannedAmount ?? 0 })),
    milestones: (p.milestones || []).map(m => ({ ...m, assignedTo: m.responsiblePerson || '' })),
    outputs: p.outputs || [],
    approvalEvents: p.approvalEvents || [],
    strategicObjectives: p.strategicObjectives || [],
    programmes: p.programmes || [],
    sections: p.sections || [],
    _sectionData: sectionData,
  };
}

function mapProjectListFromApi(projects) {
  if (!projects) return [];
  if (!Array.isArray(projects)) {
    if (Array.isArray(projects.$values)) return projects.$values.map(mapProjectFromApi);
    return [];
  }
  return projects.map(mapProjectFromApi);
}

// ══════════════════════════════════════════════════════
// SECTION DATA — extract/pack JSON from RdProjectSection records
// ══════════════════════════════════════════════════════
// Sections are auto-created by the controller on project Create.
// Each section has an ID. To save, call PUT sections/{sectionId}.
// Sections are already included in GET {id} response — no separate GET needed.

function extractFormFromSections(sections) {
  const form = {};
  for (const sec of (sections || [])) {
    if (!sec.jsonData) continue;
    try { Object.assign(form, JSON.parse(sec.jsonData)); } catch {}
  }
  if (form.objectives) { form.description = form.objectives; delete form.objectives; }
  return form;
}

// Map form fields → section number → JSON blob
const SECTION_FIELD_MAP = {
  3: ['executiveSummary', 'scopeDescription'],
  4: ['problemStatement', 'mandateAlignment', 'innovationNovelty', 'govtPriorities'],
  5: ['description', 'methodology'],
  6: ['socioeconomicImpact', 'commercialisationPotential', 'impactScore', 'feasibilityScore', 'alignmentScore'],
  7: ['existingInfrastructure', 'additionalResources', 'divisionalManager'],
  // 8 and 9 handled as special cases below (arrays/objects)
  10: ['keyReferences'],
};

function buildSectionJson(sectionNumber, form) {
  // Special case: section 8 (risks array)
  if (sectionNumber === 8) {
    const risks = (form.risks || []).filter(r => r.description?.trim());
    return risks.length > 0 ? JSON.stringify({ risks }) : null;
  }
  // Special case: section 9 (compliance answers object)
  if (sectionNumber === 9) {
    const answers = form.complianceAnswers || {};
    const hasAnswers = Object.values(answers).some(v => v);
    return hasAnswers ? JSON.stringify({ complianceAnswers: answers }) : null;
  }

  const fields = SECTION_FIELD_MAP[sectionNumber];
  if (!fields) return null;
  const data = {};
  let hasData = false;
  for (const f of fields) {
    const val = form[f === 'description' ? 'description' : f];
    if (f === 'description') {
      data.objectives = val || '';
    } else {
      data[f] = val || (f === 'govtPriorities' ? [] : '');
    }
    if (val && (Array.isArray(val) ? val.length > 0 : true)) hasData = true;
  }
  return hasData ? JSON.stringify(data) : null;
}

// ══════════════════════════════════════════════════════
// EXPORTED API
// ══════════════════════════════════════════════════════

export const rdProjectsApi = {
  // ── Core CRUD ──
  getAll: async (params) => {
    const r = await api.get(BASE, { params });
    return { ...r, data: mapProjectListFromApi(r.data) };
  },
  get: async (id) => {
    const r = await api.get(`${BASE}/${id}`);
    return { ...r, data: mapProjectFromApi(r.data) };
  },
  create: (form) => api.post(BASE, mapProjectToApi(form)),
  update: (id, form) => api.put(`${BASE}/${id}`, mapProjectToApi(form)),
  delete: (id) => api.delete(`${BASE}/${id}`),

  // ── Sections — update individual sections by ID ──
  // Sections are already in the GET {id} response — no separate GET.
  // Controller: PUT sections/{sectionId} with { jsonData, isComplete }
  saveSections: async (projectId, form, existingSections) => {
    const results = [];
    for (const sec of (existingSections || [])) {
      const json = buildSectionJson(sec.sectionNumber, form);
      if (json !== null || sec.jsonData) {
        try {
          const r = await api.put(`${BASE}/sections/${sec.id}`, {
            jsonData: json || sec.jsonData,
            isComplete: json !== null,
          });
          results.push(r.data);
        } catch (e) {
          console.warn(`Section ${sec.sectionNumber} save failed:`, e?.response?.status, e?.response?.data);
        }
      }
    }
    return results;
  },

  // ── Milestones ──
  addMilestone: (pid, form) => api.post(`${BASE}/${pid}/milestones`, {
    title: form.title || '', description: form.description || '',
    responsiblePerson: form.responsiblePerson || form.assignedTo || '',
    dueDate: form.dueDate || null, weightPercentage: parseFloat(form.weightPercentage) || 0,
    progressPercentage: parseFloat(form.progressPercentage) || 0,
    yearNumber: parseInt(form.yearNumber) || 1,
  }),
  updateMilestone: (id, form) => api.put(`${BASE}/milestones/${id}`, {
    title: form.title || '',
    description: form.description || '',
    responsiblePerson: form.responsiblePerson || form.assignedTo || '',
    startDate: form.startDate || null,
    dueDate: form.dueDate || null,
    completedDate: form.completedDate || null,
    status: form.status || 'NotStarted',
    weightPercentage: parseFloat(form.weightPercentage) || 0,
    progressPercentage: parseFloat(form.progressPercentage) || 0,
    yearNumber: parseInt(form.yearNumber) || 1,
  }),
  deleteMilestone: (id) => api.delete(`${BASE}/milestones/${id}`),

  // ── Budget Lines — controller uses /budget-lines NOT /budget ──
  addBudgetLine: (pid, form) => api.post(`${BASE}/${pid}/budget-lines`, {
    category: form.category || 'Consumables & Materials',
    customCategory: form.category === 'Other' ? (form.customCategory || null) : null,
    description: form.description || '',
    plannedAmount: parseFloat(form.budgetAmount || form.plannedAmount) || 0,
    actualAmount: parseFloat(form.actualAmount) || 0,
    yearNumber: parseInt(form.yearNumber) || 1,
    incurredDate: form.incurredDate || null,
    reference: form.reference || null,
    varianceNote: form.varianceNote || null,
  }),
  updateBudgetLine: (id, form) => api.put(`${BASE}/budget-lines/${id}`, {
    category: form.category || 'Consumables & Materials',
    customCategory: form.category === 'Other' ? (form.customCategory || null) : null,
    description: form.description || '',
    plannedAmount: parseFloat(form.budgetAmount || form.plannedAmount) || 0,
    actualAmount: parseFloat(form.actualAmount) || 0,
    yearNumber: parseInt(form.yearNumber) || 1,
    incurredDate: form.incurredDate || null,
    reference: form.reference || null,
    varianceNote: form.varianceNote || null,
  }),
  deleteBudgetLine: (id) => api.delete(`${BASE}/budget-lines/${id}`),

  // ── Outputs ──
  addOutput: (pid, form) => api.post(`${BASE}/${pid}/outputs`, {
    outputType: form.outputType || '', description: form.description || '',
    targetDate: form.targetDate || null, yearNumber: parseInt(form.yearNumber) || 1,
    isPlanned: true, status: 'Planned',
  }),

  // ── Strategy Linkage ──
  setProjectSOs: (pid, soIds) => api.put(`${BASE}/${pid}/strategic-objectives`, soIds),
  setProjectProgrammes: (pid, progIds) => api.put(`${BASE}/${pid}/programmes`, progIds),

  // ── Workflow — controller uses POST {id}/decide with RdApprovalDecisionDto ──
  submit: (pid) => api.post(`${BASE}/${pid}/submit`),
  decide: (pid, stage, decision, comments) => api.post(`${BASE}/${pid}/decide`, { stage, decision, comments }),
  // Convenience wrappers
  approve: (pid, comments) => api.post(`${BASE}/${pid}/decide`, { stage: 'Review', decision: 'Approved', comments: comments || '' }),
  returnForRevisions: (pid, comments) => api.post(`${BASE}/${pid}/decide`, { stage: 'Review', decision: 'ReturnedForRevision', comments }),
  decline: (pid, comments) => api.post(`${BASE}/${pid}/decide`, { stage: 'Review', decision: 'Declined', comments }),

  // ── Comments — controller uses /comments NOT /review-comments ──
  getComments: (pid) => safe(api.get(`${BASE}/${pid}/comments`), []),
  addComment: (pid, data) => api.post(`${BASE}/${pid}/comments`, data),

  // ── Documents ──
  getDocuments: (pid) => safe(api.get(`${BASE}/${pid}/documents`), []),
  addDocument: (pid, data) => api.post(`${BASE}/${pid}/documents`, data),
  // ── Phase C: update document metadata ──
  updateDocument: (id, data) => api.put(`${BASE}/documents/${id}`, data),
  deleteDocument: (id) => api.delete(`${BASE}/documents/${id}`),
  // ── Phase C hotfix: fetch file bytes on demand (base64 not included in detail response) ──
  getDocumentFile: (id) => api.get(`${BASE}/documents/${id}/file`),

  // ── Phase C: Deliverables (endpoints pre-exist on controller) ──
  getDeliverables: (pid) => safe(api.get(`${BASE}/${pid}/deliverables`), []),
  addDeliverable: (pid, data) => api.post(`${BASE}/${pid}/deliverables`, data),
  updateDeliverable: (id, data) => api.put(`${BASE}/deliverables/${id}`, data),
  getDeliverableFile: (id) => api.get(`${BASE}/deliverables/${id}/file`),

  // ── Phase C: Scope Extensions ──
  getScopeExtensions: (pid) => safe(api.get(`${BASE}/${pid}/scope-extensions`), []),
  createScopeExtension: (pid, data) => api.post(`${BASE}/${pid}/scope-extensions`, data),
  updateScopeExtension: (id, data) => api.put(`${BASE}/scope-extensions/${id}`, data),
  submitScopeExtension: (id) => api.post(`${BASE}/scope-extensions/${id}/submit`),
  deleteScopeExtension: (id) => api.delete(`${BASE}/scope-extensions/${id}`),

  // ── Phase C: Billing milestones ──
  getBillingMilestones: (pid) => safe(api.get(`${BASE}/${pid}/billing-milestones`), []),
  createBillingMilestone: (pid, data) => api.post(`${BASE}/${pid}/billing-milestones`, data),
  updateBillingMilestone: (id, data) => api.put(`${BASE}/billing-milestones/${id}`, data),
  deleteBillingMilestone: (id) => api.delete(`${BASE}/billing-milestones/${id}`),
  markBillingInvoiced: (id, data) => api.post(`${BASE}/billing-milestones/${id}/mark-invoiced`, data),
  markBillingPaid: (id, data) => api.post(`${BASE}/billing-milestones/${id}/mark-paid`, data),
  writeOffBilling: (id, data) => api.post(`${BASE}/billing-milestones/${id}/write-off`, data),

  // ── Risks ──
  getRisks: (pid) => safe(api.get(`${BASE}/${pid}/risks`), []),
  addRisk: (pid, data) => api.post(`${BASE}/${pid}/risks`, data),

  // ── Team ──
  getTeam: (pid) => safe(api.get(`${BASE}/${pid}/team`), []),
  addTeamMember: (pid, data) => api.post(`${BASE}/${pid}/team`, data),

  // ── Strategy Tags ──
  setStrategyTags: (pid, tags) => api.put(`${BASE}/${pid}/strategy-tags`, tags),

  // ── Phase A: KPI linkage ──
  getKpis: (pid) => safe(api.get(`${BASE}/${pid}/kpis`), []),
  setKpis: (pid, kpiIds) => api.put(`${BASE}/${pid}/kpis`, kpiIds),

  // ── Phase A: Resources ──
  getResources: (pid, yearNumber) => safe(api.get(`${BASE}/${pid}/resources`, { params: yearNumber ? { yearNumber } : {} }), []),
  createResource: (pid, data) => api.post(`${BASE}/${pid}/resources`, data),
  updateResource: (id, data) => api.put(`${BASE}/resources/${id}`, data),
  deleteResource: (id) => api.delete(`${BASE}/resources/${id}`),

  // ── Helpers ──
  extractFormFromSections,
  buildSectionJson,
};

// ── Phase A: Admin-managed role rate card (cross-project) ──
export const roleRatesApi = {
  getAll: (params) => safe(api.get('/rolerates', { params }), []),
  get: (id) => api.get(`/rolerates/${id}`),
  resolve: (code, asOf) => api.get('/rolerates/resolve', { params: { code, asOf } }),
  create: (data) => api.post('/rolerates', data),
  update: (id, data) => api.put(`/rolerates/${id}`, data),
  delete: (id) => api.delete(`/rolerates/${id}`),
};

export default rdProjectsApi;